import { useMemo, useState } from "react";
import { Button, Checkbox } from "antd";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { AlertProps, InsightProps } from "./insights";
import Loader from "../UI/Loader";
import { formatDateLong, formatISODateToYYYYMMDD } from "../../utils/dateUtils";
import { capitalizeFirst } from "../../utils/textFormattingUtils";
import AlertNotificationToggles, {
  type AlertNotificationChannel,
} from "../Alerts/AlertNotificationToggles";
import InsightExecutionsTable from "./InsightExecutionsTable";
import { AlertSection, InsightSection } from "./InsightDrawerSections";
import { useAlertHistoryQuery } from "@/hooks/useAlertHistoryQuery";
import { useAlertMutations } from "@/hooks/useAlertMutations";
import { useInsightResultsQuery } from "@/hooks/useInsightResultsQuery";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import DrawerFooter from "./DrawerFooter";
import { isInsightArchived } from "./utils";

type InsightDrawerProps = {
  type?: string;
  insight: InsightProps;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (insight: InsightProps) => void;
  onArchive?: (insight: InsightProps) => void;
  onActivate?: (insight: InsightProps) => void;
};

export default function InsightInformationDrawer({
  insight,
  isOpen,
  onClose,
  onEdit,
  onArchive,
  onActivate,
  type = "insight",
}: InsightDrawerProps) {
  const [checkedActions, setCheckedActions] = useState<string[]>([]);

  const handleActionChange = (action: string) => {
    setCheckedActions((prev) => {
      if (prev.includes(action)) {
        return prev.filter((item) => item !== action);
      }
      return [...prev, action];
    });
  };

  if (!insight) {
    return null;
  }

  const timestamp = formatDateLong(insight?.insight_result_timestamp, "en-US");
  const cadence = isInsightArchived(insight)
    ? "Archived"
    : capitalizeFirst(insight?.cadence);
  const category = capitalizeFirst(insight?.category);
  const question = insight?.av_question || "";
  const alert = type === "alert" ? insight.alert : undefined;
  const hasAlert = !!alert && Object.keys(alert).length > 0;
  const headerArray = [
    { key: "CATEGORY", value: category },
    { key: "CADENCE", value: cadence },
    { key: "LAST UPDATED", value: timestamp },
  ];

  return (
    <ResizableDrawer
      headerTitle={
        hasAlert ? "Insight & Alert Information" : "Insight Information"
      }
      headersArray={headerArray}
      placement="right"
      open={isOpen}
      sizeToggle={{
        collapsedWidth: 800,
        expandedWidth: 1250,
        initialWidth: 800,
      }}
      destroyOnHidden={true}
      closable={false}
      onClose={onClose}
      closeIcon={null}
      footer={
        type === "insight" && (
          <DrawerFooter
            insight={insight}
            question={question}
            onClose={onClose}
            onEdit={onEdit!}
            onArchive={onArchive!}
            onActivate={onActivate!}
          />
        )
      }
    >
      <div
        data-testid="insight-drawer-alert-and-info"
        className="flex flex-col mb-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        <InsightSection insight={insight} />

        {hasAlert && (
          <>
            <AlertSection alert={alert} />
            <NextActionsSection
              actions={alert.next_best_action}
              checkedActions={checkedActions}
              onActionChange={handleActionChange}
            />
          </>
        )}

        <HistorySection
          insightId={insight.id}
          alert={hasAlert ? alert : undefined}
        />

        {hasAlert && <AlertNotificationSettingsSection alert={alert} />}
      </div>
    </ResizableDrawer>
  );
}

const AlertNotificationSettingsSection = ({ alert }: { alert: AlertProps }) => {
  const { updateAlert } = useAlertMutations();
  const { isSuperuserMode } = useSuperuserMode();
  const [notifyOverrides, setNotifyOverrides] = useState<
    Partial<Record<AlertNotificationChannel, boolean>>
  >({});

  const notifyEmail = notifyOverrides.notify_email ?? !!alert.notify_email;
  const notifyPush = notifyOverrides.notify_push ?? !!alert.notify_push;

  const handleNotificationChange = (
    channel: AlertNotificationChannel,
    value: boolean,
  ) => {
    setNotifyOverrides((prev) => ({ ...prev, [channel]: value }));
    updateAlert(
      { alertId: alert.id, payload: { [channel]: value } },
      {
        onError: () =>
          setNotifyOverrides((prev) => ({ ...prev, [channel]: !value })),
      },
    );
  };

  return (
    <div
      data-testid="insight-drawer-alert-notifications-section"
      className="mb-7"
    >
      <h3 className="text-base font-medium text-black mb-2">Notifications</h3>
      <div className="flex flex-col bg-white p-4 rounded-lg shadow-md">
        <AlertNotificationToggles
          notifyEmail={notifyEmail}
          notifyPush={notifyPush}
          disabled={!(alert.is_owner || isSuperuserMode)}
          disabledReason="Only the alert's owner can change its notifications."
          onChange={handleNotificationChange}
        />
      </div>
    </div>
  );
};

const NextActionsSection = ({
  actions,
  checkedActions,
  onActionChange,
}: {
  actions?: string[];
  checkedActions: string[];
  onActionChange: (action: string) => void;
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleViewClick = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowActions(true);
    }, 1000);
  };

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <>
      {showActions ? (
        <>
          <h3 className="text-base font-medium text-black mb-2">
            Next Actions
          </h3>
          <div className="flex flex-col bg-white p-4 rounded-lg shadow-md mb-7">
            <div className="flex flex-col gap-2">
              {actions.map((action) => (
                <Checkbox
                  key={action}
                  checked={checkedActions.includes(action)}
                  onChange={() => onActionChange(action)}
                >
                  <span className="text-sm text-[#2B4752]">{action}</span>
                </Checkbox>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <h3 className="text-base font-medium text-black mb-2">
            Next Actions
          </h3>
          <div
            className="flex flex-col justify-center items-center bg-white rounded-lg shadow-md mb-7"
            style={{
              height: "150px",
              minHeight: "150px",
              maxHeight: "150px",
            }}
          >
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader
                  noMargin={true}
                  tableLoader={false}
                  width={"80px"}
                  height={"80px"}
                />
                <p className="text-[#00000073] text-xs">LOADING</p>
              </div>
            ) : (
              <Button type="primary" onClick={handleViewClick}>
                View
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
};

const HistorySection = ({
  insightId,
  alert,
}: {
  insightId: string;
  alert?: AlertProps;
}) => {
  const { executions, isLoading } = useInsightResultsQuery(insightId);
  const { evaluations } = useAlertHistoryQuery(alert?.id, !!alert);

  const triggeredDates = useMemo(() => {
    const dates = new Set<string>();
    evaluations.forEach((evaluation) => {
      if (evaluation.condition_is_met) {
        dates.add(formatISODateToYYYYMMDD(evaluation.evaluated_at));
      }
    });
    return dates;
  }, [evaluations]);

  return (
    <div data-testid="insight-drawer-history-section" className="mb-7">
      <h3 className="text-base font-medium text-black mb-2">History</h3>
      <div className="flex flex-col bg-white p-4 rounded-lg shadow-md">
        <InsightExecutionsTable
          executions={executions}
          isLoading={isLoading}
          triggeredDates={triggeredDates}
        />
      </div>
    </div>
  );
};
